import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import {
  toggleRowSelection,
  setFilter,
  clearSelection,
  selectAllRows,
  setColumnFilter,
  setSortConfig,
  setFilterDropdown,
} from '../../store/userTableSlice';
import { tableColumns } from '../../tableConfig';
import { InfraUser } from '../../types';
import { FaFilter } from 'react-icons/fa';
import translations from '../../locales/translations';
import { matchesColumnFilter } from '../../utils/columnFilterValue';
import { refreshInfraUsers, getOperationStatus, type RefreshInfraUsersError } from '../../services/api';
import api from '../../services/api';
import { useLocation } from 'react-router-dom';
import { Modal, Button, Input, Dropdown, Badge, Menu, Space, Tabs, Alert } from 'antd';
import * as XLSX from 'xlsx';
import { useSidebar } from '../../contexts/SidebarContext';
import { useTranslation } from '../../locales/useTranslation';
import { getAllInfraUsers, getFetchRecords, associateEmailForLoginId } from '../../services/api';
import { useAuthGuard } from '../../hooks/useAuthGuard';
import { useNewGui } from '../../hooks/useNewGui';
import PageHeader from '../shared/PageHeader';
import ConfirmDestructive from '../shared/ConfirmDestructive';

const { Item: MenuItem } = Menu;

interface ColumnConfig {
    key: string;
    label: string;
    visible: boolean;
}

interface ExtendRequest {
  id: string;
  UserId: string;
  ServerInfo: string;
  NumericId: number;
  ExtensionDate: string;
  Remarks: string;
}

/** Poll window for async bulk disable/enable (same idea as refresh). */
const BULK_OP_POLL_INTERVAL_MS =
  (parseInt(process.env.REACT_APP_FRONTEND_POLLING_INTERVAL_SECONDS || '2', 10) || 2) * 1000;
const BULK_OP_HARD_TIMEOUT_MS =
  (parseInt(process.env.REACT_APP_BULK_OP_HARD_TIMEOUT_SECONDS || '1800', 10) || 1800) * 1000;
const BULK_OP_MAX_POLLS = parseInt(
  process.env.REACT_APP_BULK_OP_MAX_POLLS ||
    String(Math.ceil(BULK_OP_HARD_TIMEOUT_MS / BULK_OP_POLL_INTERVAL_MS)),
  10
);
const BULK_DISABLE_OP_STORAGE_KEY = 'infraBulkDisableOperationId';
const BULK_ENABLE_OP_STORAGE_KEY = 'infraBulkEnableOperationId';

/** Refresh poll window — must cover multi-minute CCA (not the old ~60s user-gen trap). */
const REFRESH_POLL_INTERVAL_MS =
  (parseInt(process.env.REACT_APP_FRONTEND_POLLING_INTERVAL_SECONDS || '2', 10) || 2) * 1000;
const REFRESH_HARD_TIMEOUT_MS =
  (parseInt(process.env.REACT_APP_REFRESH_HARD_TIMEOUT_SECONDS || '900', 10) || 900) * 1000;
const REFRESH_MAX_POLLS = parseInt(
  process.env.REACT_APP_REFRESH_OPERATION_MAX_POLLS ||
    String(Math.ceil(REFRESH_HARD_TIMEOUT_MS / REFRESH_POLL_INTERVAL_MS)),
  10
);
const REFRESH_OP_STORAGE_KEY = 'infraRefreshOperationId';

const UserTable: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.auth.user);
  const t = useTranslation();
  const { isAuthenticated, authValidated } = useAuthGuard();

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [operationType, setOperationType] = useState<string | null>(null);
    const [remarks, setRemarks] = useState<string>('');
    const { isSidebarOpen, toggleSidebar } = useSidebar();
    const useNew = useNewGui();

    // Function to show the modal
    const showModal = (type: string) => {
        console.log("Show model clicked for operation:", type)
        setOperationType(type);
        setIsModalVisible(true);
    };

    const operationTitles: { [key: string]: string } = {
        disable: 'Disable Login',
        enable: 'Enable Login',
        extend: 'Extend Account Expiry',
        restartvnc: 'Restart VNC',
        refreshinfrausers: 'Refresh Infra Users',
        // Add more operations as needed
    };

    // Function to handle modal OK button
    const handleOk = async () => {
        console.log('OK button clicked');
        const trimmedRemarks = remarks.trim();
        const remarksRequiredOps = ['disable', 'enable', 'extend', 'restartvnc'];
        if (operationType && remarksRequiredOps.includes(operationType) && !trimmedRemarks) {
            updateStatusMessageToError('Remarks are required for this operation (audit trail).');
            return;
        }
        setIsModalVisible(false);
        if (operationType === 'disable') {
            await handleDisableLogin(trimmedRemarks);
        } else if (operationType === 'enable') {
            await handleEnableLogin(trimmedRemarks);
        } else if (operationType === 'restartvnc') {
            await handleRestartVnc(trimmedRemarks);
        } else if (operationType === 'extend') {
            await handleExtendAccountExpiry(trimmedRemarks);
        } else if (operationType === 'refreshinfrausers') {
            await handleRefreshInfraUsers(trimmedRemarks);
        }
        setRemarks('');
    };

    // Function to handle modal Cancel button
    const handleCancel = () => {
        setIsModalVisible(false);
        setRemarks('');
    };

    // Update the button click handlers to show the modal
    const handleDisableLoginClick = () => showModal('disable');
    const handleEnableLoginClick = () => showModal('enable');
    const handleRestartVncClick = () => showModal('restartvnc');
    const handleExtendAccountExpiryClick = () => showModal('extend');
    const handleRefreshInfraUsersClick = () => showModal('refreshinfrausers');

  const location = useLocation();
  const { selectedRows, filter, columnFilters, sortConfig, filterDropdown } = useSelector((state: RootState) => state.userTable);

  const [users, setUsers] = useState<InfraUser[]>([]); // Local state for users data
  const [loading, setLoading] = useState<boolean>(true); // Loading state
  const [error, setError] = useState<string | null>(null); // Error state
  const [calendarDate, setCalendarDate] = useState<string>(''); // Calendar date as a string

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusColor, setStatusColor] = useState<'blue' | 'red' | 'orange' | null>(null);
  const [refreshFailureLog, setRefreshFailureLog] = useState<string | null>(null);
  const refreshPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshPollGenerationRef = useRef(0);
  const bulkOpPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bulkOpPollGenerationRef = useRef(0);
  const [operationResults, setOperationResults] = useState<string | null>(null);
  const [enableLoginCommandsModalVisible, setEnableLoginCommandsModalVisible] = useState(false);
  const [enableLoginCommandsList, setEnableLoginCommandsList] = useState<Array<{
    userId: string; serverHost: string; sshPort: number;
    vncPort: number | null; vncNote?: string;
  }>>([]);

  const [fetches, setFetches] = useState<any[]>([]); // Array for dropdown options
  const [selectedFetchId, setSelectedFetchId] = useState<string | null>(null); // Selected dropdown value
  const [actionsOpen, setActionsOpen] = useState<boolean>(false);

  // Column configuration state
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);

  // Associate email modal state (for empty Email cells)
  const [associateEmailOpen, setAssociateEmailOpen] = useState(false);
  const [associateEmailUserId, setAssociateEmailUserId] = useState<string | null>(null);
  const [associateEmailInput, setAssociateEmailInput] = useState('');
  const [associateEmailLoading, setAssociateEmailLoading] = useState(false);
  const [associateEmailError, setAssociateEmailError] = useState<string | null>(null);

  // Function that returns default UserTable column configuration (essential columns visible)
  const getDefaultUserTableColumns = () => {
    // Essential columns - always visible by default
    const essentialColumns = [
      'ServerInfo', 'UserId', 'Email', 'Enabled',
      'PrimaryGroup', 'primaryGroupInstance', 'VNCPorts', 'AccountExpiry',
            //  'sid', 'HomeDir',
            // 'LastUpdated', 'LastLoginTime'

    ];

    // All columns available, but only essential ones visible initially
    return tableColumns.map(col => ({
      key: col.key,
      visible: essentialColumns.includes(col.key)
    }));
  };

  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    getDefaultUserTableColumns().filter(col => col.visible).map(col => col.key)
  );

  // Add new state for the checkbox
//   const [fetchExpired, setFetchExpired] = useState<boolean>(new URLSearchParams(location.search).get('expired') === 'yes');
//   const [filterOption, setFilterOption] = useState("all");

    const queryParams = new URLSearchParams(location.search);

    // Initialize filter option based on URL query parameters
    const initialFilter = queryParams.get('expired') === 'yes' 
        ? "expired"
        : queryParams.get('active') === 'yes'
        ? "active"
        : queryParams.get('expirywithin') === '30'
        ? "expiring"
        : "all";
    const [filterOption, setFilterOption] = useState<string>(initialFilter);
    useEffect(() => {
        if (isAuthenticated && authValidated) {
            fetchData(null); // Fetch data when the component mounts and user is authenticated
        }
    }, [filterOption, isAuthenticated, authValidated]);

    useEffect(() => {
      return () => {
        refreshPollGenerationRef.current += 1;
        bulkOpPollGenerationRef.current += 1;
        if (refreshPollingRef.current) {
          clearInterval(refreshPollingRef.current);
          refreshPollingRef.current = null;
        }
        if (bulkOpPollingRef.current) {
          clearInterval(bulkOpPollingRef.current);
          bulkOpPollingRef.current = null;
        }
      };
    }, []);

  // Parse ServerInfo to host and SSH port (hostname:port or legacy underscore format)
  const parseServerInfo = (serverInfo: string): { host: string; sshPort: number } => {
    if (!serverInfo || !serverInfo.trim()) return { host: '', sshPort: 22 };
    const s = serverInfo.trim();
    const lastColon = s.lastIndexOf(':');
    if (lastColon > 0 && /^\d+$/.test(s.slice(lastColon + 1))) {
      return { host: s.slice(0, lastColon), sshPort: parseInt(s.slice(lastColon + 1), 10) };
    }
    const parts = s.split('_');
    const last = parts[parts.length - 1];
    if (parts.length > 1 && !isNaN(Number(last))) {
      return { host: parts.slice(0, -1).join('.'), sshPort: parseInt(last, 10) };
    }
    return { host: s.replace(/_/g, '.'), sshPort: 22 };
  };
  // VNCPorts from Refresh is always the display number (0–700). Actual VNC port = server base + display.
  const getDisplayFromVncPorts = (vncPorts: string): number | null => {
    if (!vncPorts || typeof vncPorts !== 'string') return null;
    const match = vncPorts.trim().split(/[\s,]+/).find(p => /^\d+$/.test(p));
    if (!match) return null;
    const n = parseInt(match, 10);
    return (n >= 0 && n <= 700) ? n : null;
  };

  // Function to handle clearing the selectedRows
  const handleClearSelection = () => {
    // Dispatch the clearSelection action to reset selectedRows
    dispatch(clearSelection());
  };

  // Handle calendar change event with proper typing
  const handleCalendarChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setCalendarDate(e.target.value);
  };

  // Check if the selected date is in the future
  const isFutureDate = (dateString: string): boolean => {
    const today = new Date();
    const selectedDate = new Date(dateString);
    return selectedDate > today;
  };

  // Function to export table data to Excel
  const handleExportToExcel = async () => {
    // Log export transaction
    try {
      const exportData = {
        recordCount: filteredData.length,
        selectedFilter: filterOption,
        selectedFetchId: selectedFetchId,
        exportedColumns: visibleColumns.length
      };

      // Make API call to log export transaction
      await api.post('/transactions/log', {
        transactionType: 'USER_TABLE_EXPORT',
        source: 'UserTable',
        records: filteredData.length.toString(),
        remarks: `Exported ${filteredData.length} user records to Excel`,
        responseText: JSON.stringify(exportData)
      }, {
        withCredentials: true
      });

      console.log('Export transaction logged successfully');
    } catch (logError) {
      console.warn('Failed to log export transaction:', logError);
      // Continue with export even if logging fails
    }

    // Prepare the data
    const data = filteredData.map((user) => {
      const exportData: { [key: string]: any } = {};
      tableColumns.forEach((column) => {
        exportData[column.header] = user[column.key as keyof InfraUser];
      });
      return exportData;
    });

    // Create a new workbook and add the data
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'InfraUsers');

    // Export to Excel file with timestamped filename (local timezone)
    const now = new Date();
    const timestamp = now.getFullYear() + '-' +
      String(now.getMonth() + 1).padStart(2, '0') + '-' +
      String(now.getDate()).padStart(2, '0') + 'T' +
      String(now.getHours()).padStart(2, '0') + '-' +
      String(now.getMinutes()).padStart(2, '0') + '-' +
      String(now.getSeconds()).padStart(2, '0');
    XLSX.writeFile(workbook, `user_accounts_${timestamp}.xlsx`);

    // Show success message
    updateStatusMessageToInfo(`Exported ${filteredData.length} records to Excel`);
  };

  // Function to fetch data (either from API or local file)
  const fetchData = async (fetchid: string | null) => {
    try {
      setLoading(true);
      setError(null);

        // Initialize query
        let queryParams = [];

        // Add filter options to query parameters
        if (filterOption === "active") {
            queryParams.push("active=yes");
        } else if (filterOption === "expired") {
            queryParams.push("expired=yes");
        } else if (filterOption === "expiring") {
            queryParams.push("expirywithin=30");
        }

        // Add selected fetch ID to query parameters
        if (fetchid) {
            queryParams.push(`fetchid=${fetchid}`);
        } else if (selectedFetchId) {
            queryParams.push(`fetchid=${selectedFetchId}`);
        }

        const users = await getAllInfraUsers(queryParams );
        setUsers(users);
        console.log("my entry data for this page", users);    
    } catch (apiError) {
      console.warn('API not available, falling back to local data');
      // Fetch from local file as fallback
      const localData = (await import('../../data.json')).default;
      console.log("Local populated Data:", localData)
      setUsers(localData);
    } finally {
      setLoading(false);
    }
  };

    const fetchDropdownData = async () => {
        try {
            const fetchData = await getFetchRecords();
            setFetches(fetchData);
        } catch (error) {
            console.error("Failed to fetch dropdown data:", error);
            setFetches([]);
        }
    };

    // Load column preferences from localStorage
    useEffect(() => {
        const savedConfig = localStorage.getItem('userTableColumnConfig');
        if (savedConfig) {
            try {
                const parsedConfig = JSON.parse(savedConfig);
                setVisibleColumns(prevColumns =>
                    tableColumns
                        .filter(col => parsedConfig[col.key] !== false)
                        .map(col => col.key)
                );
            } catch (error) {
                console.warn('Failed to load column configuration from localStorage:', error);
            }
        }
    }, []);

    // Save column preferences to localStorage
    const saveColumnConfig = (newVisibleColumns: string[]) => {
        const config = tableColumns.reduce((acc, col) => {
            acc[col.key] = newVisibleColumns.includes(col.key);
            return acc;
        }, {} as Record<string, boolean>);
        localStorage.setItem('userTableColumnConfig', JSON.stringify(config));
    };

    // Handle column visibility toggle
    const handleColumnToggle = (columnKey: string) => {
        setVisibleColumns(prevVisible => {
            const newVisible = prevVisible.includes(columnKey)
                ? prevVisible.filter(key => key !== columnKey)
                : [...prevVisible, columnKey];
            saveColumnConfig(newVisible);
            return newVisible;
        });
	};

    // Reset columns to default visibility
    const resetColumnsToDefault = () => {
        const defaultColumns = getDefaultUserTableColumns();
        const visibleKeys = defaultColumns.filter(col => col.visible).map(col => col.key);
        setVisibleColumns(visibleKeys);
        saveColumnConfig(visibleKeys);
    };

    useEffect(() => {
        fetchDropdownData(); // Fetch data when the component mounts
    }, []);

    const handleFetchSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        console.log('Selected fetch ID:', e.target.value);
        setSelectedFetchId(e.target.value); // Update selected fetch ID
        fetchData(e.target.value); // Fetch data with the updated fetch ID
    };

  const handleCheckboxChange = (sid: string) => {
    dispatch(toggleRowSelection(sid));
  };

  const stopBulkOpPolling = () => {
    if (bulkOpPollingRef.current) {
      clearInterval(bulkOpPollingRef.current);
      bulkOpPollingRef.current = null;
    }
  };

  type BulkOpPollResult = {
    status: string;
    outcome?: string;
    message?: string;
    results?: Array<{ id: string | number; status: string; ok?: boolean; UserId?: string; vncPort?: number }>;
    succeeded?: number;
    failed?: number;
    total?: number;
  };

  const pollBulkOpUntilDone = (
    operationId: string,
    label: 'Disable' | 'Enable',
    storageKey: string
  ): Promise<BulkOpPollResult> => {
    const generation = ++bulkOpPollGenerationRef.current;
    const startedAt = Date.now();
    let pollCount = 0;
    let consecutiveErrors = 0;

    return new Promise((resolve) => {
      const finish = (result: BulkOpPollResult) => {
        if (generation !== bulkOpPollGenerationRef.current) return;
        stopBulkOpPolling();
        try {
          sessionStorage.removeItem(storageKey);
        } catch {
          /* ignore */
        }
        resolve(result);
      };

      const tick = async () => {
        if (generation !== bulkOpPollGenerationRef.current) return;

        if (Date.now() - startedAt > BULK_OP_HARD_TIMEOUT_MS || pollCount >= BULK_OP_MAX_POLLS) {
          finish({
            status: 'unknown',
            outcome: 'unknown',
            message: `Stopped monitoring ${label.toLowerCase()} (time limit). The server may still be running — check Audit Logs for ${label.toLowerCase()}InfraUsers.`,
          });
          return;
        }

        pollCount += 1;
        try {
          const statusResponse = await getOperationStatus(operationId);
          consecutiveErrors = 0;
          if (!statusResponse) return;

          const st = statusResponse.status;
          const outcome = statusResponse.outcome || st;
          const msg = statusResponse.message || '';
          const data = statusResponse.lastUpdate?.data || {};
          const completed = typeof data.completed === 'number' ? data.completed : undefined;
          const total = typeof data.total === 'number' ? data.total : undefined;
          const pct = Math.round(statusResponse.progress || 0);

          if (st === 'running' || st === 'pending') {
            const progressBit =
              completed != null && total != null ? `${completed}/${total}` : `${pct}%`;
            updateStatusMessageToOperationInProgress(
              msg || `${label} in progress… (${progressBit})`
            );
            return;
          }

          if (st === 'completed' || st === 'failed') {
            finish({
              status: st,
              outcome,
              message: msg,
              results: Array.isArray(data.results) ? data.results : undefined,
              succeeded: typeof data.succeeded === 'number' ? data.succeeded : undefined,
              failed: typeof data.failed === 'number' ? data.failed : undefined,
              total: typeof data.total === 'number' ? data.total : undefined,
            });
            return;
          }

          if (st === 'not_found' && pollCount > 15) {
            finish({
              status: 'unknown',
              outcome: 'unknown',
              message: `Lost track of ${label.toLowerCase()} job. Check Audit Logs — it may still have completed.`,
            });
          }
        } catch (err) {
          consecutiveErrors += 1;
          console.error(`${label} status poll error:`, err);
          if (consecutiveErrors >= 5) {
            finish({
              status: 'unknown',
              outcome: 'unknown',
              message: `Lost connection while monitoring ${label.toLowerCase()}. Check Audit Logs for actual status.`,
            });
          }
        }
      };

      void tick();
      bulkOpPollingRef.current = setInterval(tick, BULK_OP_POLL_INTERVAL_MS);
    });
  };

  const applyBulkOpOutcome = async (
    label: 'Disable' | 'Enable',
    pollResult: BulkOpPollResult,
    requests: Array<{ id?: string | number; UserId?: string; ServerInfo?: string } | null>
  ) => {
    const results = pollResult.results || [];
    const message =
      pollResult.message ||
      `${label} finished: ${pollResult.succeeded ?? '?'}/${pollResult.total ?? '?'} succeeded`;

    if (results.length > 0) {
      setOperationResults(
        formatOperationResults({
          message,
          results: results.map((r) => ({
            id: String(r.id),
            status: r.ok === false ? `FAIL: ${r.status}` : r.status,
          })),
        })
      );
    }

    const outcome = pollResult.outcome || pollResult.status;
    if (pollResult.status === 'unknown') {
      updateStatusMessageToError(message);
      return;
    }
    if (outcome === 'failed' || pollResult.status === 'failed') {
      updateStatusMessageToError(message);
    } else if (outcome === 'partially_completed') {
      updateStatusMessageToError(message);
    } else {
      updateStatusMessageToInfo(message);
    }

    if (label === 'Enable' && results.some((r) => r.ok)) {
      const list: Array<{
        userId: string;
        serverHost: string;
        sshPort: number;
        vncPort: number | null;
        vncNote?: string;
      }> = [];
      requests.forEach((req) => {
        if (!req?.id) return;
        const user = users.find((u) => u.sid === req.id || String(u.sid) === String(req.id));
        if (!user?.UserId || !user?.ServerInfo) return;
        const result = results.find((r) => String(r.id) === String(req.id));
        if (result && result.ok === false) return;
        const { host, sshPort } = parseServerInfo(user.ServerInfo);
        const vncPort = result?.vncPort != null && result.vncPort > 0 ? result.vncPort : null;
        list.push({
          userId: user.UserId,
          serverHost: host,
          sshPort,
          vncPort,
          vncNote: vncPort != null ? `port ${vncPort}` : undefined,
        });
      });
      if (list.length > 0) {
        setEnableLoginCommandsList(list);
        setEnableLoginCommandsModalVisible(true);
      }
    }

    // Enabled was updated in DB by the job — reload table so UI matches.
    try {
      await fetchData(selectedFetchId);
    } catch (e) {
      console.warn('Reload after bulk op failed:', e);
    }
  };

  const handleDisableLogin = async (remarks: string) => {
    console.log('Disable Login for: ', selectedRows, '\nRemarks: ', remarks);
    updateStatusMessageToOperationInProgress('Disable Login starting…');

    if (selectedRows.length === 0) {
      console.warn('No records selected to disable');
      return;
    }

    const disableRequests = selectedRows
      .map((sid) => {
        const user = users.find((u) => u.sid === sid);
        if (user) {
          return {
            id: user.sid,
            UserId: user.UserId,
            ServerInfo: user.ServerInfo,
            NumericId: user.NumericId,
            Remarks: remarks,
          };
        }
        return null;
      })
      .filter(Boolean);

    try {
      // Accept is fast (202); keep a short client timeout for the accept call only.
      const response = await api.post('/infrauser/disable', disableRequests, { timeout: 60_000 });

      if (response.status === 202 && response.data?.operationId) {
        const operationId = String(response.data.operationId);
        try {
          sessionStorage.setItem(BULK_DISABLE_OP_STORAGE_KEY, operationId);
        } catch {
          /* ignore */
        }
        updateStatusMessageToOperationInProgress(
          `Disable running (id …${operationId.slice(-8)}) — ${disableRequests.length} user(s)…`
        );
        handleClearSelection();
        const pollResult = await pollBulkOpUntilDone(operationId, 'Disable', BULK_DISABLE_OP_STORAGE_KEY);
        await applyBulkOpOutcome('Disable', pollResult, disableRequests);
        return;
      }

      // Legacy sync 200 response
      if (response.status === 200) {
        updateStatusMessageToInfo('Disable Login operation completed... Check results');
        if (isResponseData(response.data)) {
          setOperationResults(formatOperationResults(response.data));
        }
        await fetchData(selectedFetchId);
      } else {
        updateStatusMessageToError('Disable Login operation FAILED!!! check results');
      }
    } catch (error: any) {
      console.error('Failed to disable users:', error);
      const msg = error?.response?.data?.message || error?.message || error?.code;
      updateStatusMessageToError(
        `Disable request failed: ${msg || 'Unknown error'}. Check Audit Logs for actual status.`
      );
    }
    handleClearSelection();
  };

  const handleEnableLogin = async (remarks: string) => {
    console.log('Enable Login for: ', selectedRows);
    updateStatusMessageToOperationInProgress('Enable User Operation starting…');

    if (selectedRows.length === 0) {
      console.warn('No records selected to enable');
      return;
    }

    const enableRequests = selectedRows
      .map((sid) => {
        const user = users.find((u) => u.sid === sid);
        if (user) {
          return {
            id: user.sid,
            UserId: user.UserId,
            ServerInfo: user.ServerInfo,
            NumericId: user.NumericId,
            Remarks: remarks,
          };
        }
        return null;
      })
      .filter(Boolean);

    try {
      const response = await api.post('/infrauser/enable', enableRequests, { timeout: 60_000 });

      if (response.status === 202 && response.data?.operationId) {
        const operationId = String(response.data.operationId);
        try {
          sessionStorage.setItem(BULK_ENABLE_OP_STORAGE_KEY, operationId);
        } catch {
          /* ignore */
        }
        updateStatusMessageToOperationInProgress(
          `Enable running (id …${operationId.slice(-8)}) — ${enableRequests.length} user(s)…`
        );
        handleClearSelection();
        const pollResult = await pollBulkOpUntilDone(operationId, 'Enable', BULK_ENABLE_OP_STORAGE_KEY);
        await applyBulkOpOutcome('Enable', pollResult, enableRequests);
        return;
      }

      if (response.status === 200) {
        updateStatusMessageToInfo('Enable User Operation completed. Check results');
        const results = response.data?.results ?? [];
        const list: Array<{
          userId: string;
          serverHost: string;
          sshPort: number;
          vncPort: number | null;
          vncNote?: string;
        }> = [];
        enableRequests.forEach((req: any) => {
          const user = users.find((u) => u.sid === req.id);
          if (!user?.UserId || !user?.ServerInfo) return;
          const { host, sshPort } = parseServerInfo(user.ServerInfo);
          const result = results.find((r: any) => r.id === req.id);
          const vncPort = result?.vncPort != null && result.vncPort > 0 ? result.vncPort : null;
          list.push({
            userId: user.UserId,
            serverHost: host,
            sshPort,
            vncPort,
            vncNote: vncPort != null ? `port ${vncPort}` : undefined,
          });
        });
        setEnableLoginCommandsList(list);
        setEnableLoginCommandsModalVisible(true);
        await fetchData(selectedFetchId);
        if (isResponseData(response.data)) {
          setOperationResults(formatOperationResults(response.data));
        }
      } else {
        updateStatusMessageToError('Enable User Operation failed. Check results');
      }
    } catch (error: any) {
      console.error('Failed to enable users:', error);
      const msg = error?.response?.data?.message || error?.message || error?.code;
      updateStatusMessageToError(
        `Enable request failed: ${msg || 'Unknown error'}. Check Audit Logs for actual status.`
      );
    }
    handleClearSelection();
  };

  const handleRestartVnc = async (remarks: string) => {
    console.log('VNC Restart for: ', selectedRows, "\nRemarks: ", remarks);
    updateStatusMessageToOperationInProgress('VNC Restart operation in progress.... Wait...');

    if (selectedRows.length === 0) {
        console.warn("No records selected for vnc restart");
        return;
    }


    // Prepare the data to be sent to the backend
    const restartVncRequests = selectedRows.map((sid) => {
        const user = users.find((u) => u.sid === sid);
        if (user) {
            return {
                id: user.sid,               // Backend expects an 'id'
                UserId: user.UserId,         // UserId to cross-validate
                ServerInfo: user.ServerInfo, // Server Info to cross-validate
                VNCPorts: user.VNCPorts,    // VNCPorts for restart (only first to be restarted)
                Enabled: user.Enabled,       // Enabled status to cross-validate
                NumericId: user.NumericId,    // NumericId to cross-validate
                Remarks: remarks,            // Remarks entered in the modal
            };
        }
        return null;
    }).filter(Boolean);

    try {

        // Make an API request to disable selected users
        const response = await api.post('/infrauser/restartvnc', restartVncRequests);

        if (response.status === 200) {
            console.log("Users disabled successfully:", response.data);
            // await fetchData(selectedFetchId); // Refresh user list after successful operation
            updateStatusMessageToInfo('Restart VNC operation completed... Check results');
        } else {
            console.error("Error in restrart VNC request execution: ", response.data);
            updateStatusMessageToError('Restrart VNC operation FAILED!!! check resuls');

            setStatusMessage('Error in restrart VNC request execution');
            setStatusColor('red'); // Red for failure
        }
        if (isResponseData(response.data)) {
            // The response is of type ResponseData
            const formattedResults = formatOperationResults(response.data);
            setOperationResults(formattedResults);
        } else {
            // Handle unexpected response structure
            console.error('Unexpected response structure', response.data);
            setOperationResults('Unexpected response structure received.');
        }
    } catch (error) {
        console.error("Failed to handle restrart VNC requests :", error);
    }
    handleClearSelection()
  };

const handleExtendAccountExpiry = async (remarks: string): Promise<void> => {
    console.log('Extend Login for: ', selectedRows, "\nRemarks: ", remarks);
    updateStatusMessageToOperationInProgress('Extend Account Expiry operation in progress.... Wait...');

    if (selectedRows.length === 0) {
        console.warn("No records selected to extend expiry.");
        return;
    }

    // Prepare the data to be sent to the backend with the selected ExtensionDate from the calendar
    const extendRequests: ExtendRequest[] = selectedRows.map((sid) => {
        const user = users.find((u) => u.sid === sid);
        if (user) {
            return {
                id: user.sid,               // Backend expects an 'id'
                UserId: user.UserId,         // UserId to cross-validate
                ServerInfo: user.ServerInfo, // Server Info to cross-validate
                NumericId: user.NumericId,   // NumericId to cross-validate
                ExtensionDate: calendarDate, // Date selected from the calendar
                Remarks: remarks,            // Remarks entered in the modal
            };
        }
        return null;
    }).filter((request): request is ExtendRequest => request !== null); // Type guarding

    try {

        // Make an API request to extend account expiry for selected users
        const response = await api.post('/infrauser/extendacctexpiry', extendRequests);

        if (response.status === 200) {
            console.log("Expiry extended successfully:", response.data);
            // await fetchData(selectedFetchId); // Refresh user list after successful operation
            updateStatusMessageToInfo('Acct Expiry extennsion completed. Check resuls'); // Blue for success
        } else {
            console.error("Error in extending expiry:", response.data);
            updateStatusMessageToError('Error in extending expiry... check results'); // Red for failure
       }
        if (isResponseData(response.data)) {
            // The response is of type ResponseData
            const formattedResults = formatOperationResults(response.data);
            setOperationResults(formattedResults);
        } else {
            // Handle unexpected response structure
            console.error('Unexpected response structure', response.data);
            setOperationResults('Unexpected response structure received.');
        }


    } catch (error) {
        console.error("Failed to extend expiry for users:", error);
        updateStatusMessageToError('Error in extending expiry'); // Red for failure
    }
    handleClearSelection()
  };

  const stopRefreshPolling = () => {
    if (refreshPollingRef.current) {
      clearInterval(refreshPollingRef.current);
      refreshPollingRef.current = null;
    }
  };

  const pollRefreshUntilDone = (operationId: string): Promise<{
    status: string;
    outcome?: string;
    message?: string;
    failureLog?: string;
    numRecs?: number;
  }> => {
    const generation = ++refreshPollGenerationRef.current;
    const startedAt = Date.now();
    let pollCount = 0;
    let consecutiveErrors = 0;

    return new Promise((resolve) => {
      const finish = (result: {
        status: string;
        outcome?: string;
        message?: string;
        failureLog?: string;
        numRecs?: number;
      }) => {
        if (generation !== refreshPollGenerationRef.current) return;
        stopRefreshPolling();
        try {
          sessionStorage.removeItem(REFRESH_OP_STORAGE_KEY);
        } catch {
          /* ignore */
        }
        resolve(result);
      };

      const tick = async () => {
        if (generation !== refreshPollGenerationRef.current) return;

        if (Date.now() - startedAt > REFRESH_HARD_TIMEOUT_MS || pollCount >= REFRESH_MAX_POLLS) {
          // Monitoring stopped — do NOT claim the refresh failed.
          finish({
            status: 'unknown',
            outcome: 'unknown',
            message:
              'Stopped monitoring refresh (time limit). The server may still be running — change Data Source or reload to check for a new fetch.',
          });
          return;
        }

        pollCount += 1;
        try {
          const statusResponse = await getOperationStatus(operationId);
          consecutiveErrors = 0;
          if (!statusResponse) return;

          const st = statusResponse.status;
          const outcome = statusResponse.outcome || st;
          const msg = statusResponse.message || '';
          const data = statusResponse.lastUpdate?.data || {};

          if (st === 'running' || st === 'pending') {
            updateStatusMessageToOperationInProgress(
              msg || `Refresh in progress… (${Math.round((statusResponse.progress || 0))}%)`
            );
            return;
          }

          if (st === 'completed') {
            finish({
              status: 'completed',
              outcome,
              message: msg,
              numRecs: typeof data.numRecs === 'number' ? data.numRecs : undefined,
            });
            return;
          }

          if (st === 'failed') {
            finish({
              status: 'failed',
              outcome: 'failed',
              message: msg || 'Refresh failed',
              failureLog: data.failureLog,
              numRecs: typeof data.numRecs === 'number' ? data.numRecs : undefined,
            });
            return;
          }

          if (st === 'not_found') {
            // Brief grace: durable txn may lag; after several misses treat as unknown
            if (pollCount > 15) {
              finish({
                status: 'unknown',
                outcome: 'unknown',
                message:
                  'Lost track of refresh operation. Check Data Source for a new fetch — the job may still have completed.',
              });
            }
          }
        } catch (err) {
          consecutiveErrors += 1;
          console.error('Refresh status poll error:', err);
          if (consecutiveErrors >= 5) {
            finish({
              status: 'unknown',
              outcome: 'unknown',
              message:
                'Lost connection while monitoring refresh (not necessarily a failed refresh). Reload or change Data Source to verify.',
            });
          }
        }
      };

      tick();
      refreshPollingRef.current = setInterval(tick, REFRESH_POLL_INTERVAL_MS);
    });
  };

  const handleRefreshInfraUsers = async (remarks: string) => {
    console.log('Handle Refresh Infra Users');
    setRefreshFailureLog(null);
    stopRefreshPolling();
    updateStatusMessageToOperationInProgress('Starting refresh…');
    try {
      const start = await refreshInfraUsers(remarks, 'UserTable');
      const operationId = start.operationId || start.fetchId;
      if (!operationId) {
        throw new Error('Refresh started but no operationId returned');
      }
      try {
        sessionStorage.setItem(REFRESH_OP_STORAGE_KEY, operationId);
      } catch {
        /* ignore */
      }
      updateStatusMessageToOperationInProgress(
        `Refresh running (id …${operationId.slice(-8)}) — collecting server reports…`
      );

      const result = await pollRefreshUntilDone(operationId);

      if (result.status === 'completed') {
        setSelectedFetchId(null);
        await fetchData(null);
        dispatch(clearSelection());
        setRefreshFailureLog(null);
        const n = result.numRecs;
        const warn = result.outcome === 'completed_with_warnings';
        if (warn) {
          updateStatusMessageToError(
            result.message ||
              'Refresh finished with 0 records — check CCA/SSH reachability. Data Source may still have updated.'
          );
        } else {
          updateStatusMessageToInfo(
            n != null
              ? `Refresh completed — ${n} records. Check results.`
              : 'Refresh InfraUser completed! Check results'
          );
        }
        fetchDropdownData();
        return;
      }

      if (result.status === 'failed') {
        setRefreshFailureLog(result.failureLog || result.message || null);
        updateStatusMessageToError(result.message || 'Refresh InfraUser operation FAILED! check results');
        fetchDropdownData();
        return;
      }

      // unknown / monitoring interrupted
      setRefreshFailureLog(null);
      updateStatusMessageToOperationInProgress(
        result.message ||
          'Monitoring interrupted — refresh may still complete on the server. Change Data Source or reload.'
      );
      fetchDropdownData();
    } catch (error: any) {
      const err = error as RefreshInfraUsersError;
      console.error('Failure in Refreshing Users', err);
      const failureLog = err.failureLog ?? (err as Error).message;
      setRefreshFailureLog(failureLog || null);
      updateStatusMessageToError(
        err.message || 'Failed to start refresh. Check backend connectivity and try again.'
      );
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>, columnKey: keyof InfraUser) => {
    dispatch(setColumnFilter({ key: columnKey, value: e.target.value }));
  };

  const handleSort = (columnKey: keyof InfraUser) => {
    let direction: 'ascending' | 'descending' = 'ascending';
    if (sortConfig && sortConfig.key === columnKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    dispatch(setSortConfig({ key: columnKey, direction }));
  };

  const handleFilterDropdownToggle = (columnKey: string) => {
    dispatch(setFilterDropdown(filterDropdown === columnKey ? null : columnKey));
  };

  const updateStatusMessageToError = async (message:string) => {
            setStatusMessage(message);
            setStatusColor('red');
    };
  const updateStatusMessageToInfo = async (message:string) => {
            setStatusMessage(message);
            setStatusColor('blue');
    };
  const updateStatusMessageToOperationInProgress = async (message:string) => {
            setStatusMessage(message);
            setStatusColor('orange');
    };

interface Result {
    id: string | number;
    status: string;
}

interface ResponseData {
    message: string;
    results: Result[];
}

    // Type guard to validate ResponseData structure
    const isResponseData = (data: any): data is ResponseData => {
        return (
            typeof data === 'object' &&
            data !== null &&
            typeof data.message === 'string' &&
            Array.isArray(data.results) &&
            data.results.every((result: any): result is Result =>
                (typeof result.id === 'string' || typeof result.id === 'number') &&
                typeof result.status === 'string'
            )
        );
    };

    const formatOperationResults = (data: ResponseData): string => {
        const { message, results } = data;
        let formattedResults = `${message}\n\n`;

        results.forEach(result => {
            formattedResults += `ID: ${result.id}, Status: ${result.status}\n`;

        });
        formattedResults = formattedResults.replace(/<br\s*\/?>/gi, '\n');

        return formattedResults;
    };

  const sortedData = React.useMemo(() => {
    if (sortConfig) {
      return [...users].sort((a, b) => {
        const aValue = a[sortConfig.key] ?? '';
        const bValue = b[sortConfig.key] ?? '';

        if (aValue < bValue) {
            return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
            return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return users;
  }, [users, sortConfig]);
// console.log(sortedData,"it is sorted data");
  const filteredData = sortedData.filter((user) =>
    tableColumns.every((column) =>
      columnFilters[column.key as keyof InfraUser]
        ? matchesColumnFilter(
            user[column.key as keyof InfraUser],
            columnFilters[column.key as keyof InfraUser]!
          )
        : true
    )
  );
console.log(filteredData,"it is filtered data")
const dateFields = ["SetupDate", "AccountExpiry", "LastUpdated", "LastLoginTime"];

  if (loading) return <div
            className={`flex-1 p-6 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 transition-all duration-300 ${
                isSidebarOpen ? 'ml-64' : 'ml-20' }`}
            >Loading Users Data...</div>;
  if (error) return <p>Error loading data: {error}</p>;

  return (
    <div
        className={`flex-1 bg-white dark:bg-gray-900 transition-all duration-300 ${
                    isSidebarOpen ? 'ml-64' : 'ml-20'
        }`}
        style={{ fontFamily: "'Roboto', sans-serif" }}
    >
    {/* Page Title */}
    <div className="mb-4 px-6 pt-6">
      {useNew ? (
        <PageHeader title="Server Accounts" purpose="Find a host account and act on it." />
      ) : (
        <>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{t.serverAccountsPageTitle}</h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">Manage infrastructure user accounts and server access</p>
        </>
      )}
    </div>

    {/* Toolbar container: token-based chrome for new GUI, original slate-gradient chrome
        for old GUI. Internals (dropdowns/buttons below) are untouched in both — full
        table-chrome rework is migration slice 6, not this pass. */}
    <div className={useNew
      ? "bg-surface-raised border border-border-subtle rounded mx-6 mt-6 mb-4 p-4"
      : "bg-gradient-to-r from-slate-50 via-gray-50 to-zinc-100 dark:from-gray-800 dark:via-gray-700 dark:to-gray-600 border border-gray-200 dark:border-gray-600 rounded-lg mx-6 mt-6 mb-4 shadow-sm p-4"
    }>
      {/* Main Toolbar Row - Always Visible */}
      <div className="flex items-center justify-between flex-wrap gap-3">

          {/* Left Section: Core Operations */}
          <div className="flex items-center gap-3 flex-wrap">

            {/* Filters Dropdown */}
            <Dropdown
                menu={{
                    items: [
                    {
                        key: 'content',
                        label: (
                        <div className={useNew ? "bg-surface border border-border-subtle rounded-lg shadow-lg p-4 min-w-80" : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-4 min-w-80"}>
                            {/* content */}
                            <h4 className={useNew ? "text-sm font-semibold text-ink-primary mb-3" : "text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3"}>{useNew ? 'User Status Filters' : '📊 User Status Filters'}</h4>
                            <div className="space-y-2">
                                <label className={useNew ? "flex items-center text-sm cursor-pointer hover:bg-surface-raised p-2 rounded text-ink-primary" : "flex items-center text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-2 rounded text-gray-900 dark:text-gray-100"}>
                                <input
                                    type="radio"
                                    value="all"
                                    checked={filterOption === "all"}
                                    onChange={() => setFilterOption("all")}
                                    className={useNew ? "mr-2 accent-accent" : "mr-2 text-blue-600"}
                                />
                                All Users
                                </label>
                                <label className={useNew ? "flex items-center text-sm cursor-pointer hover:bg-surface-raised p-2 rounded text-ink-primary" : "flex items-center text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-2 rounded text-gray-900 dark:text-gray-100"}>
                                <input
                                    type="radio"
                                    value="active"
                                    checked={filterOption === "active"}
                                    onChange={() => setFilterOption("active")}
                                    className={useNew ? "mr-2 accent-accent" : "mr-2 text-blue-600"}
                                />
                                Active Only
                                </label>
                                <label className={useNew ? "flex items-center text-sm cursor-pointer hover:bg-surface-raised p-2 rounded text-ink-primary" : "flex items-center text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-2 rounded text-gray-900 dark:text-gray-100"}>
                                <input
                                    type="radio"
                                    value="expired"
                                    checked={filterOption === "expired"}
                                    onChange={() => setFilterOption("expired")}
                                    className={useNew ? "mr-2 accent-accent" : "mr-2 text-blue-600"}
                                />
                                Expired Only
                                </label>
                                <label className={useNew ? "flex items-center text-sm cursor-pointer hover:bg-surface-raised p-2 rounded text-ink-primary" : "flex items-center text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 p-2 rounded text-gray-900 dark:text-gray-100"}>
                                <input
                                    type="radio"
                                    value="expiring"
                                    checked={filterOption === "expiring"}
                                    onChange={() => setFilterOption("expiring")}
                                    className={useNew ? "mr-2 accent-accent" : "mr-2 text-blue-600"}
                                />
                                Expiring Soon
                                </label>
                            </div>
                        </div>
                        ),
                    },
                    ],
                }}
              trigger={['click']}
              placement="bottomLeft"
            >
              <Button className={useNew
                ? "!px-4 !py-2 !bg-accent !text-white font-medium rounded-lg shadow-sm !hover:bg-accent-hover !transition-all !duration-200 !border-0"
                : "!px-4 !py-2 !bg-gradient-to-r !from-blue-600 !to-blue-700 !text-white font-medium rounded-lg shadow-md !hover:from-blue-700 !hover:to-blue-800 !hover:shadow-lg !transition-all !duration-200 !border-0"
              }>
                {useNew ? 'Filters ▼' : '📊 Filters ▼'}
              </Button>
            </Dropdown>

            {/* Data Source Dropdown - Hidden for approver role */}
            {user?.role !== 'approver' && (
              <Dropdown
                menu={{
                    items: [
                        {
                            key: 'default',
                            label: (
                                <div className={useNew ? "px-4 py-2 text-sm text-ink-primary hover:bg-surface-raised cursor-pointer" : "px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"}
                                    onClick={() => handleFetchSelectionChange({ target: { value: '' } } as any)}
                                >
                                    -- Choose Data Source --
                                </div>
                            ),
                        },
                        ...Array.isArray(fetches) ? fetches
                            .filter((fetch) => fetch.fetch_tracking_NumRecs > 0)
                            .map((fetch) => ({
                                key: fetch.fetch_tracking_id,
                                label: (
                                    <div className={useNew ? "px-4 py-2 text-sm text-ink-primary hover:bg-surface-raised cursor-pointer" : "px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer"}
                                        onClick={() => handleFetchSelectionChange({ target: { value: fetch.fetch_tracking_FetchId } } as any)}
                                    >
                                        {`${fetch.fetch_tracking_FetchTime} [${fetch.fetch_tracking_NumRecs} records] (${fetch.fetch_tracking_id})`}
                                    </div>
                                ),
                            })) : []
                    ],
                }}
              trigger={['click']}
              placement="bottomLeft"
            >
              <Button className={useNew
                ? "!px-4 !py-2 !bg-surface !text-accent !border !border-accent font-medium rounded-lg !hover:bg-accent/5 !transition-all !duration-200"
                : "!px-4 !py-2 !bg-gradient-to-r !from-indigo-600 !to-indigo-700 !text-white font-medium rounded-lg shadow-md !hover:from-indigo-700 !hover:to-indigo-800 !hover:shadow-lg !transition-all !duration-200 !border-0"
              }>
                {useNew ? 'Data Source ▼' : '📂 Data Source ▼'}
              </Button>
            </Dropdown>
            )}

            {/* Actions Dropdown - Hidden for approver role */}
            {user?.role !== 'approver' && (
              <Dropdown
                open={actionsOpen}
                onOpenChange={(open, info) => {
                  if (info?.source === 'menu') return;
                  setActionsOpen(open);
                }}
                dropdownRender={() => (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={useNew ? "bg-surface border border-border-subtle rounded-lg shadow-lg p-4 min-w-80" : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg p-4 min-w-80"}
                  >
                    <h4 className={useNew ? "text-sm font-semibold text-ink-primary mb-3" : "text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3"}>{useNew ? 'Remote Server Operations' : '🔧 Remote Server Operations'}</h4>

                    {selectedRows.length === 0 ? (
                      <div className={useNew ? "bg-warning/10 border border-warning/30 rounded p-3 mb-4 text-sm" : "bg-yellow-50 dark:bg-yellow-900 border border-yellow-200 dark:border-yellow-700 rounded p-3 mb-4 text-sm"}>
                        <span className={useNew ? "text-warning" : "text-yellow-800 dark:text-yellow-200"}>{useNew ? 'Select users from table to enable operations' : '⚠️ Select users from table to enable operations'}</span>
                      </div>
                    ) : (
                      <div className={useNew ? "bg-accent/10 border border-accent/30 rounded p-3 mb-4 text-sm" : "bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700 rounded p-3 mb-4 text-sm"}>
                        <span className={useNew ? "text-accent" : "text-blue-800 dark:text-blue-200"}>{useNew ? '' : '✓ '}{selectedRows.length} user{selectedRows.length > 1 ? 's' : ''} selected</span>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <button
                        className={useNew
                          ? `p-3 rounded-lg shadow-sm transition text-sm font-medium ${selectedRows.length === 0 ? 'opacity-50 cursor-not-allowed bg-surface-raised text-ink-secondary' : 'bg-danger text-white hover:opacity-90'}`
                          : `p-3 bg-red-500 text-white rounded-lg shadow-sm hover:bg-red-600 focus:ring-2 focus:ring-red-500 transition text-sm font-medium ${selectedRows.length === 0 ? 'opacity-50 cursor-not-allowed bg-gray-400' : 'hover:shadow-md'}`
                        }
                        onClick={handleDisableLoginClick}
                        disabled={selectedRows.length === 0}
                      >
                        <div className="text-center">
                          {!useNew && <div className="text-lg mb-1">🚫</div>}
                          <div className="text-xs">Disable Login</div>
                        </div>
                      </button>

                      <button
                        className={useNew
                          ? `p-3 rounded-lg shadow-sm transition text-sm font-medium ${selectedRows.length === 0 ? 'opacity-50 cursor-not-allowed bg-surface-raised text-ink-secondary' : 'bg-success text-white hover:opacity-90'}`
                          : `p-3 bg-green-500 text-white rounded-lg shadow-sm hover:bg-green-600 focus:ring-2 focus:ring-green-500 transition text-sm font-medium ${selectedRows.length === 0 ? 'opacity-50 cursor-not-allowed bg-gray-400' : 'hover:shadow-md'}`
                        }
                        onClick={handleEnableLoginClick}
                        disabled={selectedRows.length === 0}
                      >
                        <div className="text-center">
                          {!useNew && <div className="text-lg mb-1">✅</div>}
                          <div className="text-xs">Enable Login</div>
                        </div>
                      </button>

                      <button
                        className={useNew
                          ? `p-3 rounded-lg shadow-sm transition text-sm font-medium ${selectedRows.length === 0 ? 'opacity-50 cursor-not-allowed bg-surface-raised text-ink-secondary' : 'bg-accent text-white hover:bg-accent-hover'}`
                          : `p-3 bg-blue-500 text-white rounded-lg shadow-sm hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 transition text-sm font-medium ${selectedRows.length === 0 ? 'opacity-50 cursor-not-allowed bg-gray-400' : 'hover:shadow-md'}`
                        }
                        onClick={handleRestartVncClick}
                        disabled={selectedRows.length === 0}
                      >
                        <div className="text-center">
                          {!useNew && <div className="text-lg mb-1">🖥️</div>}
                          <div className="text-xs">Restart VNC</div>
                        </div>
                      </button>
                    </div>

                    <div className={useNew ? "mt-4 pt-3 border-t border-border-subtle" : "mt-4 pt-3 border-t border-gray-200"}>
                      <h5 className={useNew ? "text-sm font-medium text-ink-primary mb-2" : "text-sm font-medium text-gray-800 mb-2"}>{useNew ? 'Extend Account Expiry' : '📅 Extend Account Expiry'}</h5>
                      <div className="space-y-2">
                        <input
                          type="date"
                          value={calendarDate}
                          onChange={handleCalendarChange}
                          className={useNew
                            ? `w-full border rounded p-2 text-sm ${selectedRows.length === 0 || !isFutureDate(calendarDate) ? 'bg-surface-raised border-border-subtle cursor-not-allowed text-ink-secondary' : 'bg-surface border-border-strong text-ink-primary'}`
                            : `w-full border border-gray-300 dark:border-gray-600 rounded p-2 text-sm ${selectedRows.length === 0 || !isFutureDate(calendarDate) ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed text-gray-500 dark:text-gray-400' : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100'}`
                          }
                          disabled={selectedRows.length === 0}
                        />
                        <button
                          className={useNew
                            ? `w-full px-3 py-2 rounded transition text-sm font-medium ${selectedRows.length === 0 || !isFutureDate(calendarDate) ? 'opacity-50 cursor-not-allowed bg-surface-raised text-ink-secondary' : 'bg-warning text-white hover:opacity-90'}`
                            : `w-full px-3 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 focus:ring-2 focus:ring-orange-500 transition text-sm font-medium ${selectedRows.length === 0 || !isFutureDate(calendarDate) ? 'opacity-50 cursor-not-allowed bg-gray-400' : ''}`
                          }
                          onClick={handleExtendAccountExpiryClick}
                          disabled={selectedRows.length === 0 || !isFutureDate(calendarDate)}
                        >
                          Extend Selected Users
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                trigger={['click']}
                placement="bottomLeft"
            >
              <Button className={useNew
                ? "!px-4 !py-2 !bg-accent !text-white font-medium rounded-lg shadow-sm !hover:bg-accent-hover !transition-all !duration-200 !border-0"
                : "!px-4 !py-2 !bg-gradient-to-r !from-teal-600 !to-teal-700 !text-white font-medium rounded-lg shadow-md !hover:from-teal-700 !hover:to-teal-800 !hover:shadow-lg !transition-all !duration-200 !border-0"
              }>
                {useNew ? 'Actions' : '🔧 Actions'} {selectedRows.length > 0 && <Badge count={selectedRows.length} className="ml-1" />} ▼
              </Button>
            </Dropdown>
            )}
          </div>


          {/* Right Section: Tools & Status */}
          <div className="flex items-center gap-3 flex-wrap">

            {/* Refresh Data Button - Hidden for approver role */}
            {user?.role !== 'approver' && (
              <Button
                className={useNew
                  ? "!px-4 !py-2 !bg-surface !text-accent !border !border-accent font-medium rounded-lg !hover:bg-accent/5 !transition-all !duration-200"
                  : "!px-4 !py-2 !bg-gradient-to-r !from-cyan-600 !to-cyan-700 !text-white font-medium rounded-lg shadow-md !hover:from-cyan-700 !hover:to-cyan-800 !hover:shadow-lg !transition-all !duration-200 !border-0"
                }
                onClick={handleRefreshInfraUsersClick}
                title="Refresh infrastructure users data from server"
              >
                {useNew ? 'Refresh Data' : '🔄 Refresh Data'}
              </Button>
            )}

            {/* Export Button */}
            <Button
              className={useNew
                ? "!px-4 !py-2 !bg-surface !text-accent !border !border-accent font-medium rounded-lg !hover:bg-accent/5 !transition-all !duration-200"
                : "!px-4 !py-2 !bg-gradient-to-r !from-green-600 !to-green-700 !text-white font-medium rounded-lg shadow-md !hover:from-green-700 !hover:to-green-800 !hover:shadow-lg !transition-all !duration-200 !border-0"
              }
              onClick={handleExportToExcel}
              title="Export current data to Excel file"
            >
              {useNew ? 'Export' : '📊 Export'}
            </Button>

            {/* Configure Columns */}
            <Button
              className={useNew
                ? "!px-4 !py-2 !bg-surface !text-accent !border !border-accent font-medium rounded-lg !hover:bg-accent/5 !transition-all !duration-200"
                : "!px-4 !py-2 !bg-gradient-to-r !from-purple-600 !to-purple-700 !text-white font-medium rounded-lg shadow-md !hover:from-purple-700 !hover:to-purple-800 !hover:shadow-lg !transition-all !duration-200 !border-0"
              }
              onClick={() => setColumnConfigOpen(true)}
              title="Configure which columns to display"
            >
              {useNew ? 'Columns' : '⚙️ Columns'}
            </Button>

            {/* Status Indicator */}
            {statusMessage && (
              <div className="flex flex-col gap-1">
                <div className={useNew
                  ? `px-3 py-2 rounded-full text-sm font-medium shadow-sm ${
                      statusColor === 'blue' ? 'bg-info/10 text-info border border-info/30' :
                      statusColor === 'red' ? 'bg-danger/10 text-danger border border-danger/30' :
                      'bg-warning/10 text-warning border border-warning/30'
                    }`
                  : `px-3 py-2 rounded-full text-sm font-medium shadow-sm ${
                      statusColor === 'blue' ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                      statusColor === 'red' ? 'bg-red-100 text-red-800 border border-red-200' :
                      'bg-orange-100 text-orange-800 border border-orange-200'
                    }`
                }>
                  {statusMessage}
                </div>
                {statusColor === 'red' && refreshFailureLog && (
                  <Alert
                    type="error"
                    message="Failure details"
                    description={
                      <pre className="text-xs whitespace-pre-wrap break-words mt-1 mb-0 max-h-32 overflow-y-auto bg-red-50 p-2 rounded border border-red-200">
                        {refreshFailureLog}
                      </pre>
                    }
                    showIcon
                    closable
                    onClose={() => setRefreshFailureLog(null)}
                    className="max-w-2xl"
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>

    {/* Scrollable table container - only table content scrolls */}
    <div className="overflow-auto max-h-[calc(100vh-200px)] mx-6">
      <div className="overflow-x-auto">
        <table className={useNew
          ? "min-w-full bg-surface border border-border-subtle rounded font-plex"
          : "min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 shadow-sm rounded-lg"
        }>
          <thead className={useNew ? "bg-surface-raised" : "bg-gray-200 dark:bg-gray-700"}>
            <tr>
              <th className={useNew ? "py-2 px-4 border-b border-border-subtle" : "py-3 px-4 border-b border-gray-300 dark:border-gray-600"}>
                <input
                  type="checkbox"
                  onChange={(e) =>
                    dispatch(
                      e.target.checked
                        ? selectAllRows(filteredData.map((user) => user.sid))
                        : clearSelection()
                    )
                  }
                  checked={
                    selectedRows.length === filteredData.length &&
                    selectedRows.length > 0
                  }
                />
              </th>
              {tableColumns
                .filter(column => visibleColumns.includes(column.key))
                .map((column, index) => (
                <th
                  key={index}
                  className={useNew
                    ? "py-2 px-4 border-b border-border-subtle text-left text-xs font-semibold uppercase tracking-wider text-ink-secondary"
                    : "py-3 px-4 border-b border-gray-300 dark:border-gray-600 text-left font-medium text-gray-800 dark:text-gray-100"
                  }
                >
                  <div className="flex flex-col">
                    <div className="flex items-center cursor-pointer" onClick={() => handleSort(column.key as keyof InfraUser)}>
                      {column.header}
                      <FaFilter
                        className="ml-2 text-gray-600 dark:text-gray-400 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFilterDropdownToggle(column.key);
                        }}
                        style={{ width: '16px', height: '16px' }}
                      />
                    </div>
                    {filterDropdown === column.key && (
                      <div className="mt-2">
                        <input
                          type="text"
                          placeholder={`${translations.en.filterBy} ${column.header}`}
                          className="p-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          value={columnFilters[column.key as keyof InfraUser] || ''}
                          onChange={(e) => handleFilterChange(e, column.key as keyof InfraUser)}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredData.map((user) => (
              <tr key={user.sid} className={useNew ? "hover:bg-surface-raised border-b border-border-subtle" : "hover:bg-gray-50 dark:hover:bg-gray-600"}>
                <td className={useNew ? "py-2 px-4" : "py-2 px-4 border-b border-gray-300 dark:border-gray-600"}>
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(user.sid)}
                    onChange={() => handleCheckboxChange(user.sid)}
                  />
                </td>
                {tableColumns
                  .filter(column => visibleColumns.includes(column.key))
                  .map((column, index) => {
                  const isEmpty = user[column.key as keyof InfraUser] === undefined ||
                    user[column.key as keyof InfraUser] === null ||
                    user[column.key as keyof InfraUser] === '';
                  const isEmailColumn = column.key === 'Email';
                  return (
                  <td key={index} className={useNew ? "py-2 px-4 text-ink-primary" : "py-2 px-4 border-b border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"}>
                    {isEmailColumn && isEmpty ? (
                      <Button
                        type="primary"
                        size="small"
                        onClick={() => {
                          setAssociateEmailUserId(user.UserId ?? null);
                          setAssociateEmailInput('');
                          setAssociateEmailError(null);
                          setAssociateEmailOpen(true);
                        }}
                      >
                        Associate email
                      </Button>
                    ) : user[column.key as keyof InfraUser] !== undefined && user[column.key as keyof InfraUser] !== null && user[column.key as keyof InfraUser] !== ''
                    ? typeof user[column.key as keyof InfraUser] === 'boolean'
                        ? user[column.key as keyof InfraUser]
                        ? translations.en.yes
                        : translations.en.no
                        : dateFields.includes(column.key as string) && !isNaN(Date.parse(user[column.key as keyof InfraUser] as string))
                        ? new Date(user[column.key as keyof InfraUser] as string).toLocaleString('en-IN', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour12: false,
                            }).replace(',', '')
                        : user[column.key as keyof InfraUser]
                    : translations.en.empty}
                  </td>
                ); })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Column Configuration Dialog */}
    <Modal
        title="Configure Visible Columns"
        open={columnConfigOpen}
        onOk={() => setColumnConfigOpen(false)}
        onCancel={() => setColumnConfigOpen(false)}
        footer={[
            <Button key="reset" onClick={resetColumnsToDefault}>
                Reset to Default
            </Button>,
            <Button key="close" onClick={() => setColumnConfigOpen(false)}>
                Close
            </Button>,
        ]}
    >
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
                Select which columns to display in the table:
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}>
                {tableColumns.map((column) => (
                    <label key={column.key} style={{ display: 'flex', alignItems: 'center', padding: '0.25rem' }}>
                        <input
                            type="checkbox"
                            checked={visibleColumns.includes(column.key)}
                            onChange={() => handleColumnToggle(column.key)}
                            style={{ marginRight: '0.5rem' }}
                        />
                        {column.header}
                    </label>
                ))}
            </div>
        </div>
    </Modal>

    {/* Associate email for Login ID (empty Email cells) */}
    <Modal
      title={associateEmailUserId ? `Associate email for Login ID: ${associateEmailUserId}` : 'Associate email'}
      open={associateEmailOpen}
      onCancel={() => {
        setAssociateEmailOpen(false);
        setAssociateEmailUserId(null);
        setAssociateEmailInput('');
        setAssociateEmailError(null);
      }}
      footer={[
        <Button
          key="cancel"
          onClick={() => {
            setAssociateEmailOpen(false);
            setAssociateEmailUserId(null);
            setAssociateEmailInput('');
            setAssociateEmailError(null);
          }}
        >
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={associateEmailLoading}
          disabled={!associateEmailInput.trim()}
          onClick={async () => {
            if (!associateEmailUserId || !associateEmailInput.trim()) return;
            setAssociateEmailError(null);
            setAssociateEmailLoading(true);
            try {
              const result = await associateEmailForLoginId(associateEmailUserId, associateEmailInput.trim());
              if (result?.success) {
                setAssociateEmailOpen(false);
                setAssociateEmailUserId(null);
                setAssociateEmailInput('');
                await fetchData(selectedFetchId ?? null);
              } else {
                setAssociateEmailError(result?.message ?? 'Request failed');
              }
            } catch (e: any) {
              const msg = e?.response?.data?.message ?? e?.message ?? 'Request failed';
              setAssociateEmailError(msg);
            } finally {
              setAssociateEmailLoading(false);
            }
          }}
        >
          Associate
        </Button>,
      ]}
    >
      <p className="text-gray-600 dark:text-gray-400 mb-2">
        Enter an email to associate with this Login ID. All server account records with this Login ID will be linked to the same unique user (for license control).
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-500 mb-3">
        If the email does not exist, a new unique user will be created. If it already exists, records will be linked to that user.
      </p>
      <Input
        type="email"
        placeholder="e.g. user@example.com"
        value={associateEmailInput}
        onChange={(e) => setAssociateEmailInput(e.target.value)}
      />
      {associateEmailError && (
        <Alert type="error" message={associateEmailError} className="mt-3" />
      )}
    </Modal>

    {useNew && operationType === 'disable' ? (
      <ConfirmDestructive
        open={isModalVisible}
        title={selectedRows.length === 1 ? 'Disable account?' : `Disable ${selectedRows.length} accounts?`}
        entityLabel={
          selectedRows.length === 1
            ? (users.find(u => u.sid === selectedRows[0])?.UserId || String(selectedRows[0]))
            : `${selectedRows.length} accounts selected`
        }
        consequence="Loses VNC + SSH access immediately. Active sessions are terminated. Recorded in audit — remarks are required."
        confirmLabel="Disable"
        requireTypedConfirmation={
          selectedRows.length === 1
            ? (users.find(u => u.sid === selectedRows[0])?.UserId || String(selectedRows[0]))
            : 'DISABLE'
        }
        remarksRequired
        remarks={remarks}
        onRemarksChange={setRemarks}
        remarksPlaceholder="Why are you disabling? (required for audit)"
        onConfirm={handleOk}
        onCancel={handleCancel}
      />
    ) : (
      <Modal
        title={operationTitles[operationType || ''] || 'Unknown Operation'} // Default to 'Unknown Operation'
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        okButtonProps={{
          disabled: ['disable', 'enable', 'extend', 'restartvnc'].includes(operationType || '') && !remarks.trim(),
        }}
      >
        <Input.TextArea
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder={
            ['disable', 'enable', 'extend', 'restartvnc'].includes(operationType || '')
              ? 'Enter remarks (required for audit)'
              : 'Enter remarks'
          }
          rows={4}
        />
      </Modal>
    )}

    <Modal
      title="Login commands (SSH & VNC)"
      open={enableLoginCommandsModalVisible}
      onCancel={() => setEnableLoginCommandsModalVisible(false)}
      footer={[
        <Button key="close" onClick={() => setEnableLoginCommandsModalVisible(false)}>Close</Button>,
      ]}
      width={720}
    >
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
        Commands for <strong>{enableLoginCommandsList.length}</strong> enabled user{enableLoginCommandsList.length !== 1 ? 's' : ''}. Scroll to see all.
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-500 mb-3">
        VNC port is resolved per server (base + display). Display comes from refresh data; base is read from server config or inferred from running VNC.
      </p>
      <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-2">
        {enableLoginCommandsList.map((entry, idx) => (
          <div key={`${entry.userId}-${entry.serverHost}-${idx}`} className="rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-3">
            <div className="font-medium text-gray-800 dark:text-gray-200 mb-2">{entry.userId} @ {entry.serverHost}</div>
            {entry.vncNote && (
              <div className="text-xs text-amber-700 dark:text-amber-400 mb-1">{entry.vncNote}</div>
            )}
            <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">SSH (with VNC tunnel):</div>
            <pre className="text-xs bg-white dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-600 overflow-x-auto">
              {entry.vncPort != null
                ? `ssh -p ${entry.sshPort} -L ${entry.vncPort}:localhost:${entry.vncPort} ${entry.userId}@${entry.serverHost}`
                : `ssh -p ${entry.sshPort} ${entry.userId}@${entry.serverHost}`}
            </pre>
            <div className="text-xs text-gray-600 dark:text-gray-400 mt-2 mb-1">VNC Login:</div>
            <pre className="text-xs bg-white dark:bg-gray-900 p-2 rounded border border-gray-200 dark:border-gray-600">
              {entry.vncPort != null ? `localhost:${entry.vncPort}` : '— (no VNC port)'}
            </pre>
          </div>
        ))}
      </div>
    </Modal>
    </div>
  );
};

export default UserTable;
